/*
 * @Author: chenchaoqiang@leedarson.com
 * @Date: 2022-08-02 17:27:05
 * Copyright 2022 Leedarson, All Rights Reserved.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePackages from '@root/hooks/giftCard/usePackages';
import { ExtendPackageItem, GiftPackageItem } from '@root/components/Package';
import { IGiftCardPackage, IGiftCardExtendPackage } from '@root/types/package';
import Button from '@root/components/Button';
import './index.less';
import { CellCard, CellTitle } from '@root/components/Cell';
import PageLoading from '@root/components/PageLoading';
import PageLoadError from '@root/components/PageLoadError';
import SafeAreaInset from '@root/components/SafeAreaInset';
import Empty from '@root/components/Empty';

const GiftCard = () => {
  const { t } = useTranslation();
  const {
    state: {
      paying,
      packages,
      extendPackages,
      loading,
      // unBindDevices,
      selectedPackage,
      selectExtendPackage,
    },
    action: { onClickPay, onSelected, onSelectedExtend, getPackages },
  } = usePackages();

  const cardBaseInfo = useMemo(() => {
    return packages[0] || ({} as IGiftCardPackage);
  }, [packages]);

  const cycle = useMemo(() => {
    if (cardBaseInfo.timeCount) {
      const key =
        cardBaseInfo.timeCount > 1
          ? `${cardBaseInfo.timeUnit}s`
          : cardBaseInfo.timeUnit;
      return `${cardBaseInfo.timeCount} ${
        t(key) || cardBaseInfo.timeUnit || ''
      }`;
    }

    return '';
  }, [cardBaseInfo.timeCount, cardBaseInfo.timeUnit, t]);

  if (loading < 1) {
    return <PageLoading />;
  }

  if (loading === 2) {
    return <PageLoadError onTryAgain={getPackages} />;
  }

  // 续订礼品卡无法确认是否需要支付 有未绑定设备 不需要支付的礼品卡
  // const btnText =
  //   selectExtendPackage?.goodsId ||
  //   unBindDevices.length ||
  //   !cardBaseInfo.payMark
  //     ? t('purchase_now')
  //     : t('pay_with_PayPal');

  return (
    <>
      <div className="flex-section-auto">
        {packages.length ? (
          <>
            <CellTitle enhance>{t('gift_card_use_1', { cycle })}</CellTitle>
            <CellCard marginTop={0} marginBottom={0}>
              {packages.map((item: IGiftCardPackage) => {
                return (
                  <GiftPackageItem
                    key={item.goodsId}
                    title={item.goodsName}
                    goodsId={item.goodsId}
                    priceList={item.goodsPriceList}
                    checkedId={selectedPackage?.goodsId}
                    description={item.description}
                    onSelected={() => onSelected(item)}
                    maxDevNum={item.maxDevNum}
                  />
                );
              })}
            </CellCard>
          </>
        ) : (
          <Empty marginTop={20}>{t('no_data')}</Empty>
        )}
        {extendPackages.length > 0 && (
          <>
            <CellTitle enhance>{t('gift_card_use_2', { cycle })}</CellTitle>
            <CellCard marginTop={0}>
              {extendPackages.map((item: IGiftCardExtendPackage) => {
                return (
                  <ExtendPackageItem
                    key={item.packageId}
                    title={item.packageName}
                    value={item.packageId}
                    expiredDays={item.expiredDays}
                    devNum={item.devNum}
                    supportedDevNum={item.supportedDevNum}
                    checkedId={selectExtendPackage?.packageId}
                    onSelected={() => onSelectedExtend(item)}
                  />
                );
              })}
            </CellCard>
          </>
        )}
      </div>
      <div className="flex-section-footer">
        {cardBaseInfo.payMark === 1 && (
          <p className="footer-tips">{t('gift_card_description')}</p>
        )}
        {packages.length > 0 || extendPackages.length > 0 ? (
          <Button
            disabled={
              !(selectedPackage?.goodsId || selectExtendPackage?.goodsId)
            }
            loading={paying}
            onClick={onClickPay}
          >
            {t('gift_card_redeem')}
          </Button>
        ) : null}

        <SafeAreaInset />
      </div>
    </>
  );
};

export default GiftCard;
